/**
 * Owner: Masa Bayu
 * File: commerce-cart.ts
 * Date:Time: 2026-09-15 22:25
 * Description: T3-E1 cart model — add/remove/qty + catalog validation (≠ payment).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { createCommerceOffer, createPriceSnapshot, } from './commerce-pricing';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
export const QXREAD_CART_LINE_ISSUES = [
    'ok',
    'unavailable',
    'invalid_publication',
    'price_changed',
];
function assertQuantity(value) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
        throw new Error('QXRead cart quantity must be an integer >= 1');
    }
    return value;
}
export function createEmptyCart(options) {
    const cartId = options?.cartId !== undefined ? options.cartId : createQxreadId('qxrdcart');
    const sessionKey = options?.sessionKey !== undefined ? options.sessionKey : createQxreadId('qxrdsess');
    assertValidId(cartId, 'QXRead cartId');
    assertValidId(sessionKey, 'QXRead cart sessionKey');
    const updatedAt = options?.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead cart updatedAt');
    return Object.freeze({
        cartId,
        sessionKey,
        lines: Object.freeze([]),
        updatedAt,
    });
}
export function offerFromCatalogEntry(entry, sellerPriceMinorUnits) {
    return createCommerceOffer({
        publicationId: entry.publicationId,
        qxpPublicationRef: entry.qxpPublicationRef,
        title: entry.title,
        sellerPriceMinorUnits,
        saleableOnQxread: entry.marketplaceReady && entry.availability === 'available',
        marketplaceReady: entry.marketplaceReady,
    });
}
export function validateOfferAgainstCatalog(offer, catalog) {
    const entry = catalog.find((item) => item.publicationId === offer.publicationId);
    if (entry === undefined) {
        return 'invalid_publication';
    }
    if (!entry.marketplaceReady || entry.availability !== 'available') {
        return 'unavailable';
    }
    if (entry.qxpPublicationRef === null || entry.qxpPublicationRef.trim() === '') {
        return 'invalid_publication';
    }
    return 'ok';
}
export function addToCart(cart, offer, catalog, quantity = 1) {
    assertNoForbiddenFoundationKey(offer, 'QXRead addToCart offer');
    const qty = assertQuantity(quantity);
    const issue = validateOfferAgainstCatalog(offer, catalog);
    if (issue === 'invalid_publication' || issue === 'unavailable') {
        throw new Error(`QXRead cannot add to cart: ${issue}`);
    }
    if (!offer.saleableOnQxread || !offer.marketplaceReady) {
        throw new Error('QXRead cannot add non-saleable offer to cart');
    }
    const existing = cart.lines.find((line) => line.publicationId === offer.publicationId);
    const snapshot = createPriceSnapshot(offer);
    let lines;
    if (existing !== undefined) {
        lines = cart.lines.map((line) => line.publicationId === offer.publicationId
            ? Object.freeze({
                ...line,
                quantity: line.quantity + qty,
                snapshot,
                issue: 'ok',
            })
            : line);
    }
    else {
        const lineId = createQxreadId('qxrdcline');
        lines = [
            ...cart.lines,
            Object.freeze({
                lineId,
                publicationId: offer.publicationId,
                quantity: qty,
                snapshot,
                issue: 'ok',
            }),
        ];
    }
    return Object.freeze({
        ...cart,
        lines: Object.freeze(lines),
        updatedAt: new Date().toISOString(),
    });
}
export function updateCartLineQuantity(cart, publicationId, quantity) {
    assertValidId(publicationId, 'QXRead cart publicationId');
    const qty = assertQuantity(quantity);
    const found = cart.lines.some((line) => line.publicationId === publicationId);
    if (!found) {
        throw new Error('QXRead cart line not found');
    }
    return Object.freeze({
        ...cart,
        lines: Object.freeze(cart.lines.map((line) => line.publicationId === publicationId ? Object.freeze({ ...line, quantity: qty }) : line)),
        updatedAt: new Date().toISOString(),
    });
}
export function removeFromCart(cart, publicationId) {
    assertValidId(publicationId, 'QXRead cart publicationId');
    return Object.freeze({
        ...cart,
        lines: Object.freeze(cart.lines.filter((line) => line.publicationId !== publicationId)),
        updatedAt: new Date().toISOString(),
    });
}
export function revalidateCart(cart, catalog, currentOffers) {
    const offerById = new Map(currentOffers.map((offer) => [offer.publicationId, offer]));
    const lines = cart.lines.map((line) => {
        const offer = offerById.get(line.publicationId);
        if (offer === undefined) {
            return Object.freeze({ ...line, issue: 'invalid_publication' });
        }
        const catalogIssue = validateOfferAgainstCatalog(offer, catalog);
        if (catalogIssue !== 'ok') {
            return Object.freeze({ ...line, issue: catalogIssue });
        }
        if (offer.sellerPriceMinorUnits !== line.snapshot.sellerPriceMinorUnits) {
            return Object.freeze({ ...line, issue: 'price_changed' });
        }
        return Object.freeze({ ...line, issue: 'ok' });
    });
    return Object.freeze({
        ...cart,
        lines: Object.freeze(lines),
        updatedAt: new Date().toISOString(),
    });
}
export function serializeCart(cart) {
    return Object.freeze({
        cartId: cart.cartId,
        sessionKey: cart.sessionKey,
        lines: Object.freeze(cart.lines.map((line) => Object.freeze({
            lineId: line.lineId,
            publicationId: line.publicationId,
            quantity: line.quantity,
            snapshot: line.snapshot,
        }))),
        updatedAt: cart.updatedAt,
    });
}
export function restoreCart(payload, catalog, currentOffers) {
    assertNoForbiddenFoundationKey(payload, 'QXRead cart persistence');
    assertValidId(payload.cartId, 'QXRead cartId');
    assertValidId(payload.sessionKey, 'QXRead cart sessionKey');
    assertTimestamp(payload.updatedAt, 'QXRead cart updatedAt');
    const base = Object.freeze({
        cartId: payload.cartId,
        sessionKey: payload.sessionKey,
        lines: Object.freeze(payload.lines.map((line) => Object.freeze({
            lineId: line.lineId,
            publicationId: line.publicationId,
            quantity: assertQuantity(line.quantity),
            snapshot: line.snapshot,
            issue: 'ok',
        }))),
        updatedAt: payload.updatedAt,
    });
    return revalidateCart(base, catalog, currentOffers);
}
export function cartHasBlockingIssues(cart) {
    return cart.lines.some((line) => line.issue !== 'ok');
}
//# sourceMappingURL=commerce-cart.js.map